// Shared type definitions (no runtime code — safe for every tier to import).
// Shapes follow TECHNICAL_DESIGN.md §2.

export type ItemId = string;
export type Rarity = 0 | 1 | 2 | 3 | 4;
export type AffixId = 'atk' | 'hp' | 'def' | 'spd' | 'crit';
export type MotifId = 'wolf' | 'ember' | 'root';
// Six equipment slots. weapon = melee (base.atk), subweapon = bow (base.atk), armor/cloak = base.def,
// necklace/ring = affix-only. Crafted gear fills weapon/subweapon/armor; the rest are dungeon loot.
export type Slot = 'weapon' | 'subweapon' | 'armor' | 'necklace' | 'ring' | 'cloak';
// Relics are dungeon-only passives; two can be worn at once (separate from the six gear slots).
export type RelicEffectId = 'regen' | 'xp' | 'dodge' | 'momentum' | 'fortune';

export type TileId = 0 | 1 | 2 | 3 | 4; // water, sand, grass, darkGrass, rock
export type Biome = 'water' | 'beach' | 'meadow' | 'forest' | 'highlands';
export type NodeKind = 'tree' | 'bush' | 'stone' | 'iron' | 'gold' | 'entrance' | 'totem';

export interface Stats {
  atk: number;
  maxHp: number;
  def: number;
  spdPct: number;
  critPct: number;
}

// ---- loot / gear ----
export type LegendaryId = 'kill_heal' | 'gold_magnet' | 'bounty' | 'night_stride' | 'chain_crit';
export interface Affix { id: AffixId; value: number }
export interface GearItem {
  uid: string;
  slot: Slot;
  name: string;
  rarity: Rarity;
  base: Partial<Stats>;
  affixes: Affix[]; // length === rarity (0..4)
  legendary?: LegendaryId; // present iff rarity >= 3
  motif?: MotifId; // ~35% of drops; stacking gradient sets
  empower: { level: number; bumps: Partial<Record<AffixId, number>> };
  floor: number;
  crafted?: string; // recipe id if this is a crafted (not looted) piece — one owned per recipe
  icon?: string; // display icon (crafted gear carries its recipe icon)
}

// A dungeon relic: a passive effect whose magnitude scales with rarity. Two worn at once.
export interface RelicItem {
  uid: string;
  effect: RelicEffectId;
  name: string;
  rarity: Rarity;
  floor: number;
}

export interface ItemDef {
  id: ItemId;
  name: string;
  icon: string; // emoji or texture-registry name
  kind: 'resource' | 'food' | 'tool' | 'placeable' | 'tome';
  stack: number;
  food?: { maxHp?: number; spdPct?: number; durationS: number };
  tool?: { type: 'axe' | 'pickaxe'; tier: 1 | 2 | 3; power: number };
}

export interface WorldNode {
  key: string; // `${cx},${cy}:${i}` — deterministic identity
  kind: NodeKind;
  tx: number;
  ty: number;
  maxHp: number;
}

export interface Chunk {
  cx: number;
  cy: number;
  tiles: Uint8Array; // 576 entries, row-major
  nodes: WorldNode[]; // seed-derived; harvested state in the save delta
}

// ---- runtime entities (game.ts owns; never serialized) ----
export interface Mob {
  eid: number;
  defId: string;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  state: 'wander' | 'chase' | 'windup' | 'attack';
  provoked: boolean;
  facing: { x: number; y: number };
  wanderUntilGameS: number;
  windupUntilGameS: number;
  attackCdUntilGameS: number;
  hitFlashUntilGameS: number;
  kbX: number; // knockback velocity, decays
  kbY: number;
  dmgScale: number; // dungeon-floor damage multiplier (1 overworld); not serialized
  isBoss: boolean;
}

export interface GroundDrop {
  eid: number;
  x: number;
  y: number;
  bornAtGameS: number;
  item: ItemId | 'gold';
  n: number;
  gear?: GearItem; // a gear drop (item/n unused when present)
}

export interface Projectile {
  eid: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  dmg: number;
  diesAtGameS: number;
}

// ---- food buff (pure resolver input) ----
export interface FoodBuff {
  itemId: ItemId;
  maxHp: number;
  spdPct: number;
  expiresAtGameS: number;
}

// ---- save schema (current version; earlier versions are subsets) ----
export const SAVE_VERSION = 11;

export type PlaceableKind = 'campfire' | 'torch' | 'wooden_wall' | 'home_portal';
export interface Placed { kind: PlaceableKind; tx: number; ty: number }

// ---- optional cloud accounts (Layer 1) — interfaces live here (pure) so ui.ts can use them
// without importing the impure cloud adapter (dependency-cruiser forbids that). ----
export type CloudStatus = 'off' | 'signed-out' | 'signing-in' | 'syncing' | 'synced' | 'error';
/** What the Account UI in Settings talks to. Backed by cloud.ts when configured, else undefined. */
export interface AccountApi {
  status: () => CloudStatus;
  email: () => string | null;
  message: () => string;
  onChange: (fn: () => void) => void; // UI re-render hook on any status change
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  push: () => void; // re-sync the local save up (called on the ~10s autosave)
}
/** What the game provides to the cloud adapter — read local save, apply a resolved winner. */
export interface CloudHooks {
  getLocalSave: () => SaveData | null;
  applySave: (s: SaveData) => void;
}

export interface SaveData {
  version: number;
  seed: number;
  createdAtMs: number;
  lastSeenMs: number;
  clock: { gameS: number };
  player: {
    x: number;
    y: number;
    hp: number;
    gold: number;
    name: string; // the wanderer's chosen name (shown on the Hero tab); '' → falls back to "Hero"
    palette: { body: number; hair: number; outfit: number };
  };
  home: { x: number; y: number }; // spawn point — the compass points here
  skills: Record<'wood' | 'mine' | 'combat' | 'craft', number>;
  // crafted tiers = the always-available baseline; equipped gear overrides when present.
  tools: { axe: number; pickaxe: number };
  weapons: { melee: number; bow: number };
  armor: number;
  gear: GearItem[]; // rolled drops + crafted pieces the player owns
  equipped: Partial<Record<Slot, string>>; // slot → uid ref into gear[]
  relics: RelicItem[]; // owned dungeon relics
  equippedRelics: (string | null)[]; // two slots → relic uid (or null)
  inventory: Record<ItemId, number>;
  buffs: { food: FoodBuff[]; warmedUntilGameS: number };
  world: {
    harvested: Record<string, number>; // nodeKey → respawnAtGameS
    placed: Placed[];
  };
  embers: { lastCollectMs: number }; // wall-clock accrual anchor (24h cap)
  dungeon: {
    deepest: number; checkpoints: number[]; pity: number;
    bests: Record<number, number>; tier: number; insideEntranceKey?: string;
  };
  satchel: { gold: number; items: Record<ItemId, number>; gear: GearItem[] }; // transient carry
  totems: [boolean, boolean, boolean];
  blessing: { lastAccrualDayUTC: number; banked: number; claimedDayUTC: number; activeId: string | null };
  sanctuary: number; // home safe-zone upgrade level (0 = base radius)
  // collection log — creatures defeated, recipes crafted, relic effects found (fills as you play)
  codex: { mobs: string[]; recipes: string[]; relics: string[] };
  // grove restoration arc — `fed` = grove relics poured in, `stage` = totems lit (boons granted)
  grove: { fed: number; stage: number };
}
